/**
 * Procura de mensagens — a parte que não fala com o servidor.
 *
 * Quem faz a procura a sério é o Postgres, com `websearch_to_tsquery` e um
 * dicionário português. O que sobra para aqui é o que o servidor NÃO devolve:
 * saber que pedaços do texto marcar a amarelo e que fatia de uma mensagem de
 * 4000 caracteres mostrar na lista de resultados.
 *
 * Está separado do componente porque as três decisões abaixo têm todas uma
 * resposta errada óbvia, e é mais barato prová-las num teste do que descobri-las
 * a olhar para o ecrã.
 */

/** Um pedaço de texto e se deve aparecer marcado. */
export interface Fatia {
  texto: string;
  realce: boolean;
}

const LETRA = /[\p{L}\p{N}]/u;

/**
 * Palavras que o dicionário português do Postgres deita fora antes de procurar.
 * Marcá-las seria pintar de amarelo metade da frase por causa de um "de" que o
 * servidor nem chegou a usar. Curta de propósito: só as que aparecem mesmo numa
 * caixa de procura.
 */
const VAZIAS = new Set([
  "as",
  "os",
  "um",
  "uma",
  "de",
  "do",
  "da",
  "dos",
  "das",
  "em",
  "no",
  "na",
  "nos",
  "nas",
  "ao",
  "aos",
  "ou",
  "que",
  "com",
  "por",
  "para",
  "se",
  "pelo",
  "pela",
]);

/**
 * Minúsculas e sem acentos, MAS com o mesmo número de caracteres do original.
 *
 * É esse o ponto todo: um `.normalize("NFD").replace(/\p{M}/gu, "")` de uma vez
 * encolhe a string ("ã" são dois pontos de código em NFD, um depois de tirar o
 * acento) e todos os índices calculados sobre ela deixam de servir para cortar
 * o texto ORIGINAL, que é o que se mostra. Normalizando caractere a caractere,
 * a posição i de um corresponde sempre à posição i do outro.
 */
function normalizar(chars: readonly string[]): string[] {
  return chars.map((c) => c.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase() || c.toLowerCase());
}

/**
 * Os termos que o utilizador escreveu, prontos a comparar.
 *
 * A sintaxe é a do `websearch_to_tsquery`, e há duas partes dela que NÃO se
 * podem marcar: `-palavra` exclui (marcar o que o servidor usou para EXCLUIR
 * seria mentir sobre o resultado) e `or` é um operador, não uma palavra. As
 * aspas agrupam uma frase, mas para efeitos de marcação valem as palavras que
 * estão lá dentro.
 */
export function termosDe(query: string): string[] {
  const limpo = query.replace(/["“”]/g, " ");
  const termos: string[] = [];

  for (const bruto of limpo.split(/\s+/)) {
    if (!bruto) continue;
    if (bruto.startsWith("-")) continue;

    // Um "e-mail" são dois lexemas para o servidor; parte-se igual aqui, senão
    // procurar por ele não marcava nada.
    for (const termo of normalizar([...bruto])
      .join("")
      .split(/[^\p{L}\p{N}]+/u)) {
      if (termo.length < 2) continue;
      if (termo === "or" || VAZIAS.has(termo)) continue;
      if (!termos.includes(termo)) termos.push(termo);
    }
  }

  return termos;
}

/**
 * A palavra corresponde ao termo?
 *
 * O servidor procura por LEXEMAS: com o dicionário português, "reuniao" encontra
 * "reuniões" e "reunir". O browser não tem stemmer nenhum, por isso uma
 * comparação exacta deixava o resultado a aparecer sem nada marcado — e uma
 * linha de procura sem nada marcado lê-se como um erro.
 *
 * Prefixo comum longo é a aproximação: chega para a família de uma palavra
 * ("reuni|ao" e "reuni|oes" partilham seis) sem juntar palavras que só se
 * parecem no princípio.
 */
function combina(palavra: string, termo: string): boolean {
  if (palavra.startsWith(termo)) return true;
  if (termo.length < 4 || palavra.length < 4) return false;

  let comum = 0;
  while (comum < termo.length && comum < palavra.length && termo[comum] === palavra[comum]) comum++;

  // Quatro caracteres iguais E quase todo o termo consumido. Sem a segunda
  // condição, procurar "contratar" marcava "conta": partilham "cont" e não têm
  // nada a ver uma com a outra.
  return comum >= 4 && comum >= termo.length - 3;
}

/**
 * Parte o texto em pedaços marcados e não marcados, pronto a desenhar.
 *
 * Pedaços seguidos com o mesmo estado saem juntos: uma marcação por palavra
 * geraria um `<span>` por caractere de pontuação no meio.
 */
export function realcar(texto: string, termos: readonly string[]): Fatia[] {
  if (!texto) return [];
  if (termos.length === 0) return [{ texto, realce: false }];

  const chars = [...texto];
  const base = normalizar(chars);
  const fatias: Fatia[] = [];

  let i = 0;
  while (i < chars.length) {
    const inicio = i;
    const ePalavra = LETRA.test(base[i] as string);
    while (i < chars.length && LETRA.test(base[i] as string) === ePalavra) i++;

    const original = chars.slice(inicio, i).join("");
    const palavra = base.slice(inicio, i).join("");
    const realce = ePalavra && termos.some((t) => combina(palavra, t));

    const ultima = fatias[fatias.length - 1];
    if (ultima && ultima.realce === realce) ultima.texto += original;
    else fatias.push({ texto: original, realce });
  }

  return fatias;
}

/**
 * A fatia da mensagem que vale a pena mostrar num resultado.
 *
 * Uma mensagem pode ter 4000 caracteres e a palavra procurada estar no fim.
 * Cortar os primeiros N é o reflexo errado: mostra um parágrafo onde a palavra
 * não aparece e o resultado parece enganado. Corta-se à volta do primeiro
 * acerto, e nas fronteiras de palavra para não partir nenhuma ao meio.
 *
 * O `max` é um alvo, não um limite duro: esticar até ao fim de uma palavra pode
 * passá-lo por uns caracteres, e é preferível a mostrar "reuni".
 */
export function excerto(texto: string, termos: readonly string[], max = 260): string {
  const chars = [...texto];
  if (chars.length <= max) return texto;

  const base = normalizar(chars);
  const posicao = primeiroAcerto(base, termos);

  // Sem acerto visível (o servidor casou por um lexema que o cliente não
  // reconhece): o princípio da mensagem é melhor do que uma fatia do meio.
  if (posicao < 0) return `${chars.slice(0, max).join("").trimEnd()}…`;

  const folga = Math.floor(max / 4);
  let inicio = Math.max(0, posicao - folga);
  let fim = Math.min(chars.length, inicio + max);
  inicio = Math.max(0, fim - max);

  while (inicio > 0 && LETRA.test(base[inicio - 1] as string)) inicio--;
  while (fim < chars.length && LETRA.test(base[fim] as string)) fim++;

  const corpo = chars.slice(inicio, fim).join("").trim();
  return `${inicio > 0 ? "…" : ""}${corpo}${fim < chars.length ? "…" : ""}`;
}

function primeiroAcerto(base: readonly string[], termos: readonly string[]): number {
  if (termos.length === 0) return -1;

  let i = 0;
  while (i < base.length) {
    if (!LETRA.test(base[i] as string)) {
      i++;
      continue;
    }
    const inicio = i;
    while (i < base.length && LETRA.test(base[i] as string)) i++;
    const palavra = base.slice(inicio, i).join("");
    if (termos.some((t) => combina(palavra, t))) return inicio;
  }

  return -1;
}

/**
 * Os filtros ESCRITOS na própria caixa, ao estilo do Discord: `de:` e `em:`.
 *
 * Substituíram dois `<select>` — um de canais, outro de membros — e a troca não
 * foi de gosto. Um selector obriga a fechar a mão que está a escrever, tirar o
 * rato do sítio, abrir uma lista e voltar; escrever `de:kawã` continua no mesmo
 * gesto. Vale sobretudo em servidores grandes, onde a lista de membros é uma
 * lista de rolar. (E não desaparece do ecrã: quem não conhece a sintaxe vê-a
 * escrita por baixo da caixa, que é onde o Discord também a põe.)
 *
 * O valor pode vir entre aspas — `de:"kawã oliveira"` — porque um nome com
 * espaço é o caso normal, não a excepção. Sem aspas, o valor é uma palavra só.
 *
 * O que sobra é o TEXTO, e só ele vai no `q`: mandar `de:kawã` para o
 * `websearch_to_tsquery` procurava a palavra "de" e a palavra "kawã" no corpo
 * das mensagens — resultados errados, sem um único erro à vista.
 */
export interface Filtros {
  /** O que procurar de facto. Pode ficar vazio: `de:kawã` sozinho é uma pergunta legítima. */
  texto: string;
  /** Nome (não id) de quem escreveu, tal como foi escrito. `null` = qualquer pessoa. */
  de: string | null;
  /** Nome do canal, sem o `#`. `null` = todos. */
  em: string | null;
}

/** `de:` e `em:`, com o valor entre aspas ou até ao primeiro espaço. */
const FILTRO = /(^|\s)(de|em):(?:"([^"]*)"|(\S*))/giu;

export function separarFiltros(entrada: string): Filtros {
  let de: string | null = null;
  let em: string | null = null;

  const texto = entrada
    .replace(FILTRO, (_todo, antes: string, chave: string, comAspas?: string, palavra?: string) => {
      const valor = (comAspas ?? palavra ?? "").trim();
      // `de:` sozinho, ainda a meio de escrever, não é um filtro — é uma pessoa
      // no meio de uma palavra. Deitá-lo fora do texto na mesma: procurar por
      // "de:" enquanto se escreve o nome dava uma lista vazia a piscar.
      if (valor) {
        if (chave.toLowerCase() === "de") de = valor;
        else em = valor;
      }
      return antes;
    })
    .replace(/\s+/g, " ")
    .trim();

  return { texto, de, em };
}

/**
 * O nome que o utilizador escreveu corresponde a este?
 *
 * Sem acentos e sem maiúsculas, e por PREFIXO: escrever o nome todo para
 * filtrar por alguém seria mais trabalho do que o selector que isto substituiu.
 * `de:kaw` chega para o Kawã.
 */
export function nomeCombina(nome: string, escrito: string): boolean {
  const limpar = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
  const alvo = limpar(escrito);
  return alvo.length > 0 && limpar(nome).startsWith(alvo);
}
